import { useEffect } from 'react';
import { useWidgetSettings } from '@/context/PropertyContext';
import type { LuxurySettings } from '@experiencelocal/types/widget';

/**
 * Component that dynamically injects luxury customization CSS
 * Applies luxury fonts, spacing, and interaction styles to the booking widget
 */
export function LuxuryStyles() {
  const widgetSettings = useWidgetSettings();
  const luxury = ('luxury' in widgetSettings ? widgetSettings.luxury : undefined) as LuxurySettings | undefined;

  useEffect(() => {
    if (!luxury) return;

    const root = document.documentElement;

    // Apply luxury font settings
    if (luxury.headingFont !== 'Default') {
      root.style.setProperty('--font-heading', `'${luxury.headingFont}', serif`);

      // Load Google Font
      const linkId = 'luxury-heading-font';
      let link = document.getElementById(linkId) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.id = linkId;
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }
      const fontName = luxury.headingFont.replace(/ /g, '+');
      link.href = `https://fonts.googleapis.com/css2?family=${fontName}:wght@400;500;600;700&display=swap`;
    }

    if (luxury.bodyFont !== 'Default') {
      root.style.setProperty('--font-body', `'${luxury.bodyFont}', sans-serif`);
      root.style.setProperty('--font-sans', `'${luxury.bodyFont}', sans-serif`);

      // Load Google Font
      const linkId = 'luxury-body-font';
      let link = document.getElementById(linkId) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.id = linkId;
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }
      const fontName = luxury.bodyFont.replace(/ /g, '+');
      link.href = `https://fonts.googleapis.com/css2?family=${fontName}:wght@400;500;600;700&display=swap`;
    }

    // Apply font scale
    const scaleMultiplier = {
      compact: '0.9',
      comfortable: '1.0',
      spacious: '1.1',
    }[luxury.fontScale];
    root.style.setProperty('--font-scale', scaleMultiplier);

    // Apply density classes
    const bodyClasses = document.body.classList;

    // Remove existing luxury classes
    bodyClasses.forEach((className) => {
      if (className.startsWith('luxury-')) {
        bodyClasses.remove(className);
      }
    });

    // Add new luxury classes
    bodyClasses.add(`luxury-density-${luxury.density}`);
    bodyClasses.add(`luxury-card-padding-${luxury.cardPadding}`);
    bodyClasses.add(`luxury-section-spacing-${luxury.sectionSpacing}`);
    bodyClasses.add(`luxury-image-${luxury.imageTreatment}`);
    if (luxury.imageOverlayGradient) {
      bodyClasses.add('luxury-image-overlay');
    }
    bodyClasses.add(`luxury-image-hover-${luxury.imageHoverEffect}`);
    bodyClasses.add(`luxury-button-hover-${luxury.buttonHoverEffect}`);
    bodyClasses.add(`luxury-card-transition-${luxury.cardTransition}`);
    bodyClasses.add(`luxury-loading-${luxury.loadingAnimation}`);

    // Cleanup on unmount
    return () => {
      root.style.removeProperty('--font-heading');
      root.style.removeProperty('--font-body');
      root.style.removeProperty('--font-sans');
      root.style.removeProperty('--font-scale');

      // Remove Google Font links
      document.getElementById('luxury-heading-font')?.remove();
      document.getElementById('luxury-body-font')?.remove();

      // Remove luxury classes
      bodyClasses.forEach((className) => {
        if (className.startsWith('luxury-')) {
          bodyClasses.remove(className);
        }
      });
    };
  }, [luxury]);

  return null;
}
